import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Container,
  Skeleton,
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball';
import PersonIcon from '@mui/icons-material/Person';


const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#FFFFFF", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#FFFFFF", "#FFFFFF"],
  DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#FEC430", "#FFFFFF"],
  STL: ["#BE0A15", "#FDDB00", "#FFFFFF"],
  LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
  "0": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};

export default function Trending(props: any) {
  const [pData, setPData] = useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [lastUpdated, setLastUpdated] = React.useState('');
  const [frame, setFrame] = useState<string>("Week");

  useEffect(() => {
    fetch(props.prod ? "https://oriolebird.pythonanywhere.com/trending-pages/" + frame : "/trending-pages/" + frame)
      .then((res) => res.json())
      .then((pData) => {
        console.log("Trending: ", pData)
        setPData(pData.data)
        setLoading(false);
        setLastUpdated(pData.last_updated);
      });
  }, [props.prod, frame]);

  const adjustedDate = new Date(lastUpdated);
  adjustedDate.setHours(adjustedDate.getHours() - 4);

  const getHeadshotUrl = (playerId: string) => {
    return `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_213,q_auto:best/v1/people/${playerId}/headshot/67/current`;
  };

  const gridSize = () => {
    if (!props.isDesktop) return 12; // Full width on mobile
    return 6; // 2 per row on desktop
  };

  const frames = ["Day", "Week",  "Month"]

  const handleFrame = (event: SelectChangeEvent) => {
    setFrame(event.target.value as string);
  };


  return (
    <Container
      style={{
        padding: "24px",
        width: 1000,
        border: "#293241 solid 2px",
        boxShadow:
          "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
        maxWidth: "95%",
        backgroundColor: "#FFFFFF",
        marginBottom: "24px",
      }}
    >
      <title>Trending Players - Prospect Savant</title>
      <Box sx={{ mt: 2 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap'
        }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            Trending Players from Past
            <Select
            variant="standard"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={frame}
              label="Timeframe"
              onChange={handleFrame}
              defaultValue="weekly"
              sx={{marginLeft: "15px", fontWeight: "bold", fontSize: "18pt"}}
            >
              {frames.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </Typography>

          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Updated: {adjustedDate.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'America/New_York' // Eastern Time (ET)
              })} ET
            </Typography>
          )}
        </Box>

        <Grid container spacing={2}>
          {loading ? (
            // Loading skeleton - wider version
            Array.from({ length: 5 }).map((_, index) => (
              <Grid item xs={12} key={index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <Skeleton variant="circular" width={80} height={80} />
                    <Box sx={{ ml: 3, flex: 1, minWidth: 0 }}>
                      <Skeleton variant="text" width="40%" height={30} />
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Skeleton variant="text" width="60px" height={24} />
                        <Skeleton variant="text" width="60px" height={24} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Skeleton variant="circular" width={20} height={20} />
                        <Skeleton variant="text" width="80px" height={20} sx={{ ml: 1 }} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : pData.length > 0 ? (
            // Player cards - wider version
            pData.map((player: any) => (
              <Grid item xs={gridSize()} key={player.player_id}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
                    }
                  }}
                >
                  <a href={player.url.includes("afl") ? "/afl/player/" + player.player_id: "/player/" + player.player_id}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* Larger headshot */}
                      <CardMedia
                        component="img"
                        image={getHeadshotUrl(player.player_id)}
                        alt={player.title}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/default-headshot.jpg';
                        }}
                        sx={{
                          width: 70,
                          height: 107,
                          borderRadius: '20%',
                          objectFit: 'cover',
                          border: `3px solid ${teamColors[player.team]?.[0] || '#ccc'}`
                        }}
                      />

                      <Box sx={{
                        ml: 3,
                        flex: 1,
                        minWidth: 0, // Crucial for text truncation
                      }}>
                        {/* Player name with truncation */}
                        <Typography
                          variant="h6"
                          component="a"
                          href={"/player/" + player.player_id}
                          sx={{
                            fontWeight: 'bold',
                            textDecoration: 'none',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            '&:hover': {
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {player.title}
                        </Typography>

                        <Box sx={{
                          display: 'flex',
                          gap: 2,
                          mt: 1.5,
                          alignItems: 'center'
                        }}>
                          {/* Team badge */}
                          <Chip
                            label={player.team}
                            size="medium"
                            sx={{
                              backgroundColor: teamColors[player.team]?.[0] || '#ccc',
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '0.9rem',
                              minWidth: 60,
                              height: 28
                            }}
                          />

                          {/* Position badge */}
                          <Chip
                            label={player.position}
                            size="medium"
                            icon={<SportsBaseballIcon sx={{ fontSize: '16px', color: "#FFF" }} />}
                            sx={{
                              backgroundColor: teamColors[player.team]?.[0] || '#666',
                              fontSize: '0.9rem',
                              color: "#FFF",
                              minWidth: 80,
                              height: 28
                            }}
                          />
                        </Box>

                        {/* Visits count - larger */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
                          <PersonIcon sx={{ fontSize: '1.2rem', mr: 1 }} />
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {player.visits.toLocaleString()} views
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </a>
                </Card>
              </Grid>
            ))
          ) : (
            // Empty state
            <Grid item xs={12}>
              <Box sx={{
                textAlign: 'center',
                p: 4,
                borderRadius: 1
              }}>
                <Typography variant="body1" color="text.secondary">
                  No trending players data available
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Container>
  );
}